import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import CollapsiblePanel from "../CollapsiblePanel.jsx";

// Status config — two new states: review_pending and finalizing.
// generating: call 1 in progress (authoring, no audio)
// review_pending: call 1 done; bundle awaits coordinator review
// finalizing: call 2 in progress (TTS + spec)
// ready: call 2 done; output_id available
const STATUS_CONFIG = {
  none:           { label: "Not generated",   color: "var(--color-text-tertiary)", bg: "var(--color-bg-subtle)" },
  generating:     { label: "Authoring…",      color: "var(--color-status-draft)",  bg: "#fef3e2" },
  review_pending: { label: "Awaiting review", color: "#7c3aed",                    bg: "rgba(139,92,246,0.08)" },
  finalizing:     { label: "Finalizing…",     color: "var(--color-status-draft)",  bg: "#fef3e2" },
  ready:          { label: "Ready",           color: "var(--color-status-live)",   bg: "var(--color-accent-subtle)" },
  failed:         { label: "Failed",          color: "var(--color-critical)",      bg: "var(--color-critical-bg)" },
};

function parseStructuredError(errorStr) {
  if (!errorStr) return null;
  try {
    const obj = JSON.parse(errorStr);
    if (obj.error_code === "composition_failed") return obj;
  } catch {}
  return null;
}

export default function PlaythroughPanel({ clientId }) {
  const [pt, setPt]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  // When set, the confirm-first + snapshot dialog is open. Shape:
  //   { finalized: bool }  — finalized true when a ready (viewable) playthrough
  //   exists, so the snapshot would freeze the live version.
  const [confirmReauthor, setConfirmReauthor] = useState(null);
  // Optional name typed into the confirm dialog for the pre-re-author snapshot.
  const [snapshotName, setSnapshotName] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`/api/clients/${clientId}/playthrough`);
      if (!res.ok) {
        // Non-OK (e.g. a transient 401 when the session is invalidated by a
        // logout in another tab). Do NOT overwrite the last-known status with an
        // error body — that would drop us out of the "generating" poll and make
        // the UI falsely read "not generated". Leave pt intact and try again.
        return;
      }
      const data = await res.json();
      setPt(data);
    } catch { /* network hiccup: keep last-known state, poll will retry */ }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Poll while authoring (call 1) or finalizing (call 2)
  useEffect(() => {
    const active = pt?.status === "generating" || pt?.status === "finalizing" || !!pt?.rewriting;
    if (!active) return;
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [pt?.status, fetchStatus]);

  // Fire the actual generate/re-author. Destroys the current bundle server-side
  // and rebuilds from the client profile.
  async function generate() {
    setTriggering(true);
    try {
      await fetch(`/api/clients/${clientId}/playthrough/generate`, { method: "POST" });
      setPt(prev => ({ ...prev, status: "generating", error: null }));
    } catch (e) {
      alert("Could not start generation: " + e.message);
    } finally {
      setTriggering(false);
    }
  }

  // Entry point for the Author / Re-author buttons. Re-authoring destroys an
  // existing playthrough, so when one exists we confirm first (with a snapshot
  // offer). First-time authoring (nothing to destroy) proceeds directly.
  function requestGenerate() {
    const s = pt?.status;
    // Destructive cases: a finalized/ready playthrough, or an authored bundle
    // awaiting review. Both would be discarded and rebuilt. First-time author
    // (none/failed — nothing durable to lose) proceeds directly.
    const willDestroy = s === "ready" || s === "review_pending";
    if (willDestroy) {
      setSnapshotName("");
      setConfirmReauthor({ finalized: s === "ready" });
    } else {
      generate();  // first-time author — nothing to lose
    }
  }

  // Snapshot the current (finalized/draft) version, then re-author. Uses the
  // optional name typed in the dialog; blank → no label (shows the timestamp).
  async function snapshotThenGenerate() {
    const label = snapshotName.trim();
    setConfirmReauthor(null);
    setTriggering(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/playthrough/snapshots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(label ? { label } : {}),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const proceed = window.confirm(
          `The snapshot could not be saved (${d.error || "unknown error"}). Re-author anyway?`
        );
        if (!proceed) { setTriggering(false); return; }
      }
    } catch (e) {
      const proceed = window.confirm("The snapshot could not be saved: " + e.message + ". Re-author anyway?");
      if (!proceed) { setTriggering(false); return; }
    }
    await generate();
  }

  function generateWithoutSnapshot() {
    setConfirmReauthor(null);
    generate();
  }

  if (loading) return null;

  const status     = pt?.status || "none";
  const cfg        = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const isStale    = pt?.is_stale;
  const structured = parseStructuredError(pt?.error);
  const isTransient  = structured?.failure_kind === "transient";
  const isStructural = structured?.failure_kind === "structural";

  // A scene rewrite (Oct 20) is call 1 running too: nothing else may start.
  const isGenerating   = status === "generating" || !!pt?.rewriting;
  const isFinalizing   = status === "finalizing";
  const isReviewPending = status === "review_pending";

  const btnLabel = isGenerating || triggering ? "Authoring…"
    : (status === "failed" && isTransient)    ? "Retry"
    : status === "ready"                       ? "Re-author"
    : "Author";

  // One of the hub's collapsible cards, like Impressions and Who's using
  // (David, Oct 11): same title style, closed on a new client, the status
  // badge on the closed row so the state reads without opening it.
  return (
    <CollapsiblePanel id="playthrough" scope={clientId} title="Introduction stream"
      badge={<span style={{ ...styles.statusBadge, color: cfg.color, background: cfg.bg }}>
        {cfg.label}{isStale && status === "ready" && " · stale"}
      </span>}>
     <div style={styles.panel}>

      {/* ── Structured error surface ───────────────────────────────────────── */}
      {status === "failed" && isTransient && (
        <div style={styles.errorBox}>
          <p style={styles.errorTitle}>Generation failed — a transient error.</p>
          {structured.detail && <p style={styles.errorDetail}>{structured.detail}</p>}
          <p style={styles.errorMeta}>
            {structured.failed_attribute && `Attribute: ${structured.failed_attribute}`}
            {structured.scene_id && structured.failed_attribute && " · "}
            {structured.scene_id && `Scene: ${structured.scene_id}`}
          </p>
        </div>
      )}
      {status === "failed" && isStructural && (
        <div style={{ ...styles.errorBox, ...styles.errorBoxStructural }}>
          <p style={styles.errorTitle}>Can't generate as configured.</p>
          {structured.detail && <p style={styles.errorDetail}>{structured.detail}</p>}
          <p style={styles.errorMeta}>
            {structured.failed_attribute && `Attribute: ${structured.failed_attribute}`}
            {structured.scene_id && structured.failed_attribute && " · "}
            {structured.scene_id && `Scene: ${structured.scene_id}`}
          </p>
          <p style={styles.errorHint}>Review the client's profile, then try again.</p>
        </div>
      )}
      {status === "failed" && !structured && pt?.error && (
        <p style={styles.errorPlain}>{pt.error}</p>
      )}

      {/* ── Review-pending prompt ─────────────────────────────────────────── */}
      {isReviewPending && (
        <div style={styles.reviewPrompt}>
          <p style={styles.reviewPromptText}>
            The introduction stream has been authored. Review the narration before finalizing.
          </p>
        </div>
      )}

      {/* ── Actions ───────────────────────────────────────────────────────── */}
      <div style={styles.actions}>
        {/* Author / Re-author button — always present except during review */}
        {!isReviewPending && (
          <button
            style={{
              ...styles.generateBtn,
              ...(triggering || isGenerating ? styles.btnDisabled : {}),
            }}
            onClick={requestGenerate}
            disabled={triggering || isGenerating}
          >
            {btnLabel}
          </button>
        )}

        {/* Review narration → shown when review is pending */}
        {isReviewPending && (
          <>
            <Link
              to={`/coordinator/clients/${clientId}/playthrough-script`}
              style={styles.reviewBtn}
            >
              Review narration →
            </Link>
            <button
              style={{ ...styles.reauthorBtn }}
              onClick={requestGenerate}
              disabled={triggering}
              title="Discard this authoring and re-author from scratch"
            >
              Re-author
            </button>
          </>
        )}

        {/* View — shown when ready */}
        {status === "ready" && (
          <Link
            to={`/clients/${clientId}/playthrough?preview=1`}
            style={styles.viewPlayBtn}
            target="_blank"
            rel="noopener noreferrer"
          >
            View ↗
          </Link>
        )}

        {/* Edit narration — shown when ready; returns to review gate */}
        {status === "ready" && (
          <Link
            to={`/coordinator/clients/${clientId}/playthrough-script`}
            style={styles.editNarrationBtn}
          >
            Edit narration
          </Link>
        )}

      </div>

      {/* ── Edited-since-finalized callout ──────────────────────────────── */}
      {status === "ready" && pt?.edited_since_finalized && (
        <div style={styles.editedSinceBanner}>
          Draft has edits that aren't live yet —{" "}
          <Link to={`/coordinator/clients/${clientId}/playthrough-script`}
            style={{ color: "#d97706", fontWeight: "var(--weight-semibold)", textDecoration: "none" }}>
            re-finalize
          </Link>{" "}to update the stream.
        </div>
      )}

      {/* ── Hint text ─────────────────────────────────────────────────────── */}
      {isGenerating && (
        <p style={styles.hint}>Authoring content — this can take a few minutes. This page updates automatically.</p>
      )}
      {isFinalizing && (
        <p style={styles.hint}>Generating audio and building the stream — this can take a few minutes. This page updates automatically.</p>
      )}
      {isStale && status === "ready" && (
        <p style={styles.hint}>Profile was updated after this stream was generated. Consider regenerating.</p>
      )}

      {confirmReauthor && (
        <div style={styles.overlay} onClick={e => e.target === e.currentTarget && setConfirmReauthor(null)}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Re-author from scratch?</h3>
            <p style={styles.modalBody}>
              This discards the current stream and re-authors all scenes from
              the client profile. Any narration edits, image choices, and on-screen
              text in the current stream will be replaced.
            </p>
            <p style={styles.modalBody}>
              {confirmReauthor.finalized
                ? "Save a snapshot of the current live version first so you can restore it if needed?"
                : "Save a snapshot of the current draft first so you can restore it if needed?"}
            </p>
            <label style={styles.modalLabel}>
              Snapshot name (optional)
              <input
                style={styles.modalInput}
                value={snapshotName}
                onChange={e => setSnapshotName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") snapshotThenGenerate(); }}
                placeholder="e.g. Before profile update"
                maxLength={120}
                autoFocus
              />
            </label>
            <div style={styles.modalActions}>
              <button style={styles.modalPrimary} onClick={snapshotThenGenerate} disabled={triggering}>
                Snapshot &amp; re-author
              </button>
              <button style={styles.modalSecondary} onClick={generateWithoutSnapshot} disabled={triggering}>
                Re-author without snapshot
              </button>
              <button style={styles.modalGhost} onClick={() => setConfirmReauthor(null)} disabled={triggering}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CollapsiblePanel>
  );
}

const styles = {
  panel: { display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)" },
  heading: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)" },
  statusBadge: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", borderRadius: "var(--radius-full)", padding: "2px var(--space-3)" },
  errorBox: { background: "var(--color-critical-bg)", border: "1px solid var(--color-critical)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  errorBoxStructural: { borderColor: "var(--color-border)", background: "var(--color-bg-subtle)" },
  errorTitle: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-critical)", margin: 0 },
  errorDetail: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", margin: 0 },
  errorMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 },
  errorHint: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", margin: 0, marginTop: "var(--space-1)", fontStyle: "italic" },
  errorPlain: { fontSize: "var(--text-xs)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  reviewPrompt: { background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)" },
  reviewPromptText: { fontSize: "var(--text-sm)", color: "#5b21b6", margin: 0, lineHeight: "var(--leading-normal)" },
  actions: { display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" },
  generateBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  reviewBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "#7c3aed", textDecoration: "none", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", whiteSpace: "nowrap" },
  reauthorBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  viewPlayBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "#fff", background: "var(--color-status-live)", textDecoration: "none", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", whiteSpace: "nowrap", marginLeft: "auto" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  editNarrationBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", textDecoration: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", whiteSpace: "nowrap" },
  editedSinceBanner: { background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", fontSize: "var(--text-xs)", color: "#92400e" },
  hint: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", lineHeight: "var(--leading-normal)" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "var(--space-4)" },
  modal: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", maxWidth: "440px", width: "100%", display: "flex", flexDirection: "column", gap: "var(--space-3)", boxShadow: "0 12px 32px rgba(0,0,0,0.18)" },
  modalTitle: { fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", color: "var(--color-critical)", margin: 0 },
  modalBody: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0 },
  modalLabel: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  modalInput: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  modalActions: { display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" },
  modalPrimary: { width: "100%", fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-5)", cursor: "pointer" },
  modalSecondary: { width: "100%", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "none", color: "var(--color-text-primary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-5)", cursor: "pointer" },
  modalGhost: { width: "100%", fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "var(--space-2)" },
};
